// SENTINEL: 2_Structured - Database Service (Persistence, Sync)
// Vitality: Healthy
import { User, Idea, Message } from "../types";
import { googleDriveService } from "./googleDriveService";
import { offlineStorage } from "./offlineStorageService";
import { logger } from "./loggingService";
import { syncStatusService } from "./syncStatusService";

let dbFileId: string | null = null;
const LOCAL_STORAGE_KEY = 'cc_guest_data_v1';
const DATA_CHANGE_KEY = 'cc_data_changed';

let cachedData: { ideas: Idea[], messages: Message[] } = { ideas: [], messages: [] };
let tabId: string | null = null;
let idbInitialized = false;

const getTabId = () => {
    if (!tabId) {
        tabId = `tab_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    }
    return tabId;
};

const notifyDataChange = (source: 'local' | 'drive') => {
    try {
        localStorage.setItem(DATA_CHANGE_KEY, JSON.stringify({
            tabId: getTabId(),
            source,
            timestamp: Date.now()
        }));
    } catch { }
};

const initializeIDB = async () => {
    if (idbInitialized) return;
    try {
        await offlineStorage.init();
        const migration = await offlineStorage.migrateFromLocalStorage();
        if (migration.migrated) {
            logger.info('System', `Migrated ${migration.count} projects from localStorage to IndexedDB`);
        }
        await offlineStorage.migrateToAccountNamespace();
        idbInitialized = true;
    } catch (e) {
        logger.warn('System', 'IndexedDB initialization failed, using localStorage fallback', { error: (e as any)?.message });
    }
};

const syncToIDB = async () => {
    if (!idbInitialized) return;
    try {
        for (const idea of cachedData.ideas) {
            await offlineStorage.saveIdea(idea);
        }
        for (const message of cachedData.messages) {
            await offlineStorage.saveMessage(message);
        }
    } catch (e) {
        logger.warn('System', 'Failed to sync to IndexedDB', { error: (e as any)?.message });
    }
};

export const dbService = {

    isConfigured() {
        return googleDriveService.isConfigured;
    },

    setClientId(id: string) {
        googleDriveService.setClientId(id);
    },

    isCloudMode() {
        return dbFileId !== null;
    },

    async init(onReady: (data: { ideas: Idea[]; messages: Message[] }, restoredUser?: User | null) => void) {
        await initializeIDB();
        const localData = await this.loadLocalData();

        try {
            await googleDriveService.initClients(async () => {
                // Try to restore previous session
                let restoredUser: User | null = null;
                try {
                    restoredUser = await googleDriveService.tryRestoreSession();
                    if (restoredUser) {
                        // Load Drive data if session was restored
                        await this.loadDriveData();
                        const driveData = cachedData;
                        onReady(driveData, restoredUser);
                        return;
                    }
                } catch (e) {
                    logger.warn('System', 'Session restore failed', { error: (e as any)?.message });
                }
                onReady(localData, null);
            });
        } catch (e) {
            console.warn("Drive init failed (expected in sandbox/preview), falling back to local storage.", e);
            onReady(localData, null);
        }
        logger.info('Database', 'DB Service Initialized', { cloudMode: this.isCloudMode() });
    },

    async login(): Promise<User> {
        const user = await googleDriveService.signIn();
        await this.loadDriveData();
        return user;
    },

    async logout() {
        await googleDriveService.logout();
        dbFileId = null;
        await this.loadLocalData();
    },

    async loadLocalData() {
        const startTime = Date.now();
        if (idbInitialized) {
            try {
                const [ideas, messages] = await Promise.all([
                    offlineStorage.getAllIdeas(),
                    offlineStorage.getAllMessages()
                ]);
                if (ideas.length > 0 || messages.length > 0) {
                    cachedData = { ideas, messages };
                    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(cachedData));
                    return cachedData;
                }
            } catch (e) {
                logger.warn('System', 'Failed to load from IndexedDB, falling back to localStorage');
            }
        }

        const local = localStorage.getItem(LOCAL_STORAGE_KEY);
        if (local) {
            try {
                cachedData = JSON.parse(local);
            } catch (e) {
                console.error("Failed to parse local data", e);
                cachedData = { ideas: [], messages: [] };
            }
        } else {
            cachedData = { ideas: [], messages: [] };
        }
        return cachedData;
    },

    async loadDriveData() {
        const startTime = Date.now();
        try {
            const { data, fileId } = await googleDriveService.loadDatabase();
            dbFileId = fileId;
            cachedData = {
                ideas: data.ideas || [],
                messages: data.messages || []
            };

            // --- AUTO-HYDRATE RAG ---
            // When we load data from Cloud, we pump it into the local Vector DB
            // The RAG server handles deduplication.
            if (cachedData.ideas.length > 0) {
                import('./ragService').then(async ({ ragService }) => {
                    logger.info('System', `Hydrating RAG with ${cachedData.ideas.length} projects from Drive...`);
                    for (const idea of cachedData.ideas) {
                        await ragService.ingestProject(idea);
                    }
                    logger.info('System', 'RAG Hydration Complete');
                }).catch(e => console.error("RAG Hydration Failed", e));
            }

            return cachedData;
        } catch (e) {
            logger.error('Database', 'Failed to load from Drive', { error: (e as any)?.message });
            console.error("Failed to load data from Drive", e);
            throw e;
        }
    },

    async loadData() {
        if (dbFileId) {
            return this.loadDriveData();
        } else {
            return await this.loadLocalData();
        }
    },

    async saveData() {
        const startTime = Date.now();
        localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(cachedData));

        syncToIDB().catch(() => { });

        if (dbFileId) {
            try {
                syncStatusService.startSync();
                await googleDriveService.saveDatabase(dbFileId, cachedData);
                syncStatusService.syncSuccess();
                notifyDataChange('drive');
            } catch (e) {
                const errorMsg = (e as Error)?.message || 'Unknown sync error';
                syncStatusService.syncFailed(errorMsg);
                logger.warn('Sync', 'Drive save failed, data saved locally', { error: errorMsg });
                notifyDataChange('local');
            }
        } else {
            notifyDataChange('local');
        }
        notifyDataChange('local');
        const durationMs = Date.now() - startTime;
        logger.debug('Database', 'Data Saved', { durationMs, cloudMode: !!dbFileId, ideas: cachedData.ideas.length });
    },

    async getUserData(userId: string) {
        return cachedData;
    },

    async saveIdea(userId: string, idea: Idea) {
        if (dbFileId && !syncStatusService.isOfflineMode()) {
            googleDriveService.syncIdeaToDoc(idea).then(result => {
                if (result) {
                    let needsUpdate = false;
                    if (result.docId && result.docId !== idea.googleDocId) {
                        idea.googleDocId = result.docId;
                        needsUpdate = true;
                    }
                    if (result.folderId && result.folderId !== idea.googleFolderId) {
                        idea.googleFolderId = result.folderId;
                        needsUpdate = true;
                    }
                    if (needsUpdate) {
                        const index = cachedData.ideas.findIndex(i => i.id === idea.id);
                        if (index >= 0) {
                            cachedData.ideas[index] = idea;
                        }
                        this.saveData();
                    }
                }
            }).catch(err => {
                logger.warn('Sync', 'Doc sync failed, will retry when online', { error: (err as Error)?.message });
                syncStatusService.syncFailed((err as Error)?.message || 'Doc sync error');
            });

            const projectMessages = cachedData.messages.filter(m => m.ideaId === idea.id);
            if (projectMessages.length > 0 && idea.googleFolderId) {
                googleDriveService.syncChatHistoryToDoc(idea, cachedData.messages).then(chatDocId => {
                    if (chatDocId && chatDocId !== idea.googleChatDocId) {
                        idea.googleChatDocId = chatDocId;
                        const index = cachedData.ideas.findIndex(i => i.id === idea.id);
                        if (index >= 0) cachedData.ideas[index] = idea;
                        this.saveData();
                    }
                }).catch(err => console.error("Chat Sync Error", err));
            }
        }

        const index = cachedData.ideas.findIndex(i => i.id === idea.id);
        if (index >= 0) cachedData.ideas[index] = idea;
        else cachedData.ideas.push(idea);

        await this.saveData();
        logger.info('Database', 'Idea Saved/Updated', { ideaId: idea.id, title: idea.title });
    },

    async deleteIdea(userId: string, ideaId: string) {
        cachedData.ideas = cachedData.ideas.filter(i => i.id !== ideaId);
        cachedData.messages = cachedData.messages.filter(m => m.ideaId !== ideaId);

        if (idbInitialized) {
            offlineStorage.deleteIdea(ideaId).catch(() => { });
        }

        await this.saveData();
        logger.info('Database', 'Idea Deleted', { ideaId });
    },

    async saveMessage(userId: string, message: Message) {
        const index = cachedData.messages.findIndex(m => m.id === message.id);
        if (index >= 0) cachedData.messages[index] = message;
        else cachedData.messages.push(message);

        await this.saveData();

        if (message.ideaId && dbFileId) {
            const idea = cachedData.ideas.find(i => i.id === message.ideaId);
            if (idea && idea.googleFolderId) {
                googleDriveService.syncChatHistoryToDoc(idea, cachedData.messages).then(chatDocId => {
                    if (chatDocId && chatDocId !== idea.googleChatDocId) {
                        idea.googleChatDocId = chatDocId;
                        this.saveData();
                    }
                }).catch(err => console.error("Chat Sync Error", err));
            }
        }
    },

    async deleteMessage(userId: string, messageId: string) {
        cachedData.messages = cachedData.messages.filter(m => m.id !== messageId);

        if (idbInitialized) {
            offlineStorage.deleteMessage(messageId).catch(() => { });
        }

        await this.saveData();
        logger.info('Database', 'Message Deleted', { messageId });
    },

    async uploadExportToProject(idea: Idea, fileName: string, blob: Blob, mimeType: string): Promise<boolean> {
        if (!dbFileId || !idea.googleFolderId) return false;

        const fileId = await googleDriveService.uploadExportToProject(idea, fileName, blob, mimeType);
        return !!fileId;
    },

    getProjectDrivePath(projectTitle: string): string {
        return googleDriveService.getProjectDrivePath(projectTitle);
    },

    getSession: async () => null,
    subscribe: () => () => { },
    getMode: () => 'cloud' as const
};